// Shared dashboard utilities — IST formatting, capital calc, remark generation
// Used by TrailBActive, SwingScanActive, SectorTradeActive, PreMarketAuto tabs

// ─────────────────────────────────────────────────────────────
// IST Timestamp Formatting
// ─────────────────────────────────────────────────────────────
export function fmtIST(utcTs, opts = {}) {
  if (!utcTs) return '—';
  
  // Handle both timestamptz (with +00 or Z) and naive UTC-stored timestamps
  // If no timezone info, assume UTC (server writes UTC to timestamp columns)
  const hasTZ = utcTs.includes('Z') || utcTs.includes('+') || utcTs.match(/[+-]\d{2}:\d{2}$/);
  const isoStr = hasTZ ? utcTs : utcTs + 'Z';
  
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '—';
  
  const { showDate = true, showYear = false, seconds = false } = opts;
  
  const options = {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit', minute: '2-digit', hour12: false
  };
  if (showDate) { options.day = '2-digit'; options.month = 'short'; }
  if (showYear) options.year = 'numeric';
  if (seconds) options.second = '2-digit';
  
  return d.toLocaleString('en-IN', options).replace(',', '');
}

// Just the time portion in IST
export function fmtISTTime(utcTs) {
  return fmtIST(utcTs, { showDate: false });
}

// Just the date portion in IST (for grouping)
export function fmtISTDate(utcTs) {
  if (!utcTs) return '—';
  const hasTZ = utcTs.includes('Z') || utcTs.includes('+');
  const d = new Date(hasTZ ? utcTs : utcTs + 'Z');
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric'
  });
}

// Is this timestamp today (in IST)?
export function isTodayIST(utcTs) {
  if (!utcTs) return false;
  const hasTZ = utcTs.includes('Z') || utcTs.includes('+');
  const d = new Date(hasTZ ? utcTs : utcTs + 'Z');
  const nowIST = new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  const dIST = d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  return nowIST === dIST;
}

// ─────────────────────────────────────────────────────────────
// Currency Formatting
// ─────────────────────────────────────────────────────────────
export function fmtRs(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const num = Number(v);
  const sign = num >= 0 ? '+' : '';
  return `${sign}₹${Math.abs(num).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.replace('+₹', '₹').replace(sign === '' ? '' : sign + '₹', sign === '+' ? '+₹' : '-₹');
}

// Simpler variant: just rupee-formatted number
export function fmtRsSimple(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const num = Number(v);
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

// P&L color: green for positive, red for negative, gray for zero/null
export function pnlColor(v) {
  if (v === null || v === undefined || isNaN(v)) return 'var(--t3)';
  const n = Number(v);
  if (n > 0) return '#0d7f3f';
  if (n < 0) return '#c92a2a';
  return 'var(--t2)';
}

// Percentage formatting
export function fmtPct(v, digits = 2) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const n = Number(v);
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}%`;
}

// ─────────────────────────────────────────────────────────────
// Capital & Slots Calculations
// ─────────────────────────────────────────────────────────────
export const STRATEGY_CONFIG = {
  trailb: {
    perSlot: 125_000,
    maxSlots: 4,
    totalCapital: 500_000,
    name: 'TrailB Active v1.0',
    description: 'Path A/B swing trader on 10-stock PL Trailblazers watchlist. Cycle-aligned.',
    rules: 'SL -2% · Time cap 7d · TREND_BREAK on daily EMA9<EMA20',
  },
  swingscan: {
    // 2026-08-21: was 15 slots / Rs15L / stale rules text. The dashboard was
    // reporting 122% deployed against a limit that had not been current since
    // the 18-slot change, and the rules line described the pre-14-Aug ladder.
    perSlot: 100_000,
    maxSlots: 18,
    totalCapital: 1_800_000,
    name: 'SwingScan Active v1.0',
    description: '669-ticker Trendlyne universe, Intelligence Framework v1.1.12. MOMENTUM + DIP setups.',
    rules: 'SL -5% · BE Lock at +2% (0.5x ATR floor) · Half Book at +2.5% · Trail 1.0x ATR · Time cap 4d at 15:05',
  },
  sectortrade: {
    perSlot: 150_000,
    maxSlots: 10,
    totalCapital: 1_500_000,
    name: 'SectorTrade Active v1.0',
    description: 'Top-down sector rotation. Trade top-3 momentum sectors, ~8 stocks each.',
    rules: 'SL -2% · BE Lock at +1% · Trail at +2.5% · Time cap 5d · VOLUME_FADE disabled',
  },
  premarket: {
    perSlot: 100_000,
    maxSlots: 15,  // shared with SwingScan
    totalCapital: 1_500_000,
    name: 'Pre-market Auto v2.0',
    description: 'Pre-open auction plays. Places paper orders at 9:04 IST based on gap/imbalance.',
    rules: 'IEP limit for MODERATE · IEP × 1.005 for HIGH · SL -2% from fill · Time cap same-day',
  },
};

// Compute capital metrics from open positions
export function computeCapitalMetrics(strategyKey, openPositions) {
  const cfg = STRATEGY_CONFIG[strategyKey];
  if (!cfg) return null;
  
  const deployed = (openPositions || []).reduce((sum, p) => {
    const buy = Number(p.buy_value || p.value || (p.entry_price * p.qty) || 0);
    return sum + buy;
  }, 0);
  
  const slotsUsed = (openPositions || []).length;
  
  return {
    totalCapital: cfg.totalCapital,
    perSlot: cfg.perSlot,
    maxSlots: cfg.maxSlots,
    slotsUsed,
    slotsAvailable: cfg.maxSlots - slotsUsed,
    deployed,
    available: cfg.totalCapital - deployed,
    deployedPct: (deployed / cfg.totalCapital) * 100,
  };
}

// ─────────────────────────────────────────────────────────────
// Auto-generated Remarks
// ─────────────────────────────────────────────────────────────
export function generateRemarks({ lifetime, todayClosed, openPositions, strategyKey }) {
  const working = [];
  const watch = [];
  
  const lifetimeWR = lifetime?.win_rate_pct || 0;
  const todayNet = (todayClosed || []).reduce((s, p) => s + Number(p.net_pnl || 0), 0);
  const openCount = (openPositions || []).length;
  
  // Positive signals
  if (lifetimeWR >= 40 && (lifetime?.lifetime_realized_net || 0) > 0) {
    working.push('Consistent WR + positive lifetime P&L');
  }
  if (todayNet > 2000) {
    working.push(`Strong day: +₹${todayNet.toFixed(0)} realized`);
  }
  
  // Biggest winner today
  const winners = (todayClosed || []).filter(p => Number(p.net_pnl) > 500);
  if (winners.length > 0) {
    const biggest = winners.sort((a, b) => Number(b.net_pnl) - Number(a.net_pnl))[0];
    working.push(`Winner: ${biggest.ticker} +₹${Math.round(biggest.net_pnl)}`);
  }
  
  // Warning signals
  if (todayNet < -3000) {
    watch.push(`Down day: ${Math.round(Math.abs(todayNet))} realized loss`);
  }
  if (openCount === 0 && strategyKey !== 'premarket') {
    watch.push('No open positions — strategy inactive');
  }
  
  // Cluster warning (SwingScan/SectorTrade)
  const nowMs = Date.now();
  const clusterEntries = (openPositions || []).filter(p => {
    if (!p.entry_ts) return false;
    const et = new Date(p.entry_ts.includes('Z') ? p.entry_ts : p.entry_ts + 'Z');
    return (nowMs - et.getTime()) < 30 * 60 * 1000;  // last 30 min
  });
  if (clusterEntries.length >= 5) {
    watch.push(`${clusterEntries.length} entries in last 30 min — cluster risk`);
  }
  
  return { working, watch };
}
